import { useEffect, useState, useCallback, useMemo } from "react";
import api, { downloadCsv } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dt, inr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Download, Filter } from "lucide-react";
import { toast } from "sonner";

export default function DepositReport() {
  const { user } = useAuth();
  const showMerchant = user.role !== "merchant";
  const [data, setData] = useState(null);
  const [providers, setProviders] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", status: "all", provider: "all", merchant_id: "all", search: "" });

  const load = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.get("/reports/deposits", { params }).then((r) => { setData(r.data); setProviders(r.data.providers || []); setMerchants(r.data.merchants || []); }).catch(() => setData({ rows: [], totals: {} }));
  }, [f]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const exp = () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    downloadCsv("/reports/deposits/export", params, `deposits_report_${Date.now()}.csv`).catch(() => toast.error("Export failed"));
  };

  const t = data?.totals || {};
  const series = useMemo(() => {
    const m = {};
    (data?.rows || []).forEach((r) => {
      if (r.status !== "success") return;
      const day = (r.created_at || "").slice(0, 10);
      if (!day) return;
      m[day] = (m[day] || 0) + (r.amount || 0);
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([date, amount]) => ({ date: date.slice(5), amount: Math.round(amount) }));
  }, [data]);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Deposit transactions report</h2>
          <p className="text-sm text-muted-foreground">Reconciled UPI collections with provider fees.</p>
        </div>
        <Button variant="outline" onClick={exp} data-testid="deposit-export-button"><Download className="mr-1.5 h-4 w-4" /> Excel</Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="space-y-1"><Label className="text-xs">Date from</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="dep-date-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Date to</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="dep-date-to" /></div>
          <div className="space-y-1"><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="dep-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["all", "success", "pending", "utr_submitted", "failed", "expired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Bank provider</Label>
            <Select value={f.provider} onValueChange={(v) => setF({ ...f, provider: v })}>
              <SelectTrigger data-testid="dep-provider"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{providers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">UTR / Order ID</Label><Input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Search…" data-testid="dep-search" /></div>
          {showMerchant && (
            <div className="space-y-1"><Label className="text-xs">Account owner</Label>
              <Select value={f.merchant_id} onValueChange={(v) => setF({ ...f, merchant_id: v })}>
                <SelectTrigger data-testid="dep-merchant"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All owners</SelectItem>{merchants.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="mt-3"><Button size="sm" onClick={load} data-testid="dep-apply"><Filter className="mr-1.5 h-4 w-4" /> Apply filters</Button></div>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4"><p className="text-xs text-muted-foreground">Transactions</p><p className="font-mono text-xl font-bold" data-testid="dep-total-count">{t.count ?? "—"}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Final amount</p><p className="font-mono text-xl font-bold text-success">{inr(t.final_amount)}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Provider fee</p><p className="font-mono text-xl font-bold text-warning">{inr(t.provider_fee)}</p></Card>
      </div>

      {series.length > 0 && (
        <Card className="p-4" data-testid="deposit-volume-chart">
          <p className="text-sm font-semibold mb-3">Daily deposit volume</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={series} margin={{ left: 4, right: 4, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={52} />
              <Tooltip formatter={(v) => inr(v)} cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Processed</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Bank provider</TableHead>
                <TableHead>Order / UTR</TableHead>
                {showMerchant && <TableHead>Merchant</TableHead>}
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Fee %</TableHead>
                <TableHead className="text-right">Provider fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {data && data.rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No transactions.</TableCell></TableRow>}
              {data && data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`deposit-row-${r.order_id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(r.created_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(r.confirmed_at)}</TableCell>
                  <TableCell className="text-xs">{r.method}</TableCell>
                  <TableCell className="text-sm font-medium">{r.provider}</TableCell>
                  <TableCell><div className="font-mono text-xs">{r.order_id}</div><div className="font-mono text-[11px] text-muted-foreground">{r.utr || "—"}</div></TableCell>
                  {showMerchant && <TableCell className="text-xs">{r.merchant_name || "—"}</TableCell>}
                  <TableCell className="text-right font-mono text-sm font-medium">{inr(r.amount)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.fee_percent ? `${r.fee_percent}%` : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-warning">{inr(r.platform_fee)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
